import { UiOption } from '@/lib/helpers';
import { PolicyModelV1 } from './policy-v1.model';
import { ControlActionCategories, ControlCategory, getActionLevel } from './controls';

export type SensitiveDataEntity = {
  type: string;
  scanType: number;
  enabled: boolean;
};

type SdActionValue = 'keep' | 'warn' | 'tag' | 'mask' | 'delete' | 'synthesize' | 'block';

export type SensitiveDataConfig = {
  action: SdActionValue;
  detect: boolean;
  entities: SensitiveDataEntity[];
};

type SdAction = { label: string; value: SdActionValue; level: number; category: ControlCategory };

const RedactActionOptions: UiOption[] = [
  { label: 'Synthesize', value: 'synthesize' },
  { label: 'Tag', value: 'tag' },
  { label: 'Mask', value: 'mask' },
  { label: 'Delete', value: 'delete' },
];

const RedactSdActions: SdAction[] = RedactActionOptions.map(
  (option, index) =>
    ({
      ...option,
      category: 'redact',
      level: index + 1,
    }) as SdAction
);

export const SensitiveDataActions: SdAction[] = [
  { label: 'Monitor', value: 'keep', category: 'allow', level: 1 },
  { label: 'Warn', value: 'warn', category: 'allow', level: 2 },
  ...RedactSdActions,
  { label: 'Block', value: 'block', category: 'disallow', level: 1 },
];

export const getSensitiveDataActionLevel = (value: SdActionValue): number => {
  return getActionLevel(value, SensitiveDataActions);
};

export const SensitiveDataActionOptions: UiOption[] = [
  { label: 'Allow', value: 'allow', meta: { header: true } },
  { label: 'Monitor', value: 'keep' },
  { label: 'Warn', value: 'warn' },
  { label: 'Redact', value: 'redact', meta: { header: true } },
  ...RedactActionOptions,
  { label: 'Disallow', value: 'disallow', meta: { header: true } },
  { label: 'Block', value: 'block' },
];

type ScanTypeConfig = Record<number, { label: string; description: string; tooltip?: string }>;

export enum SensitiveDataCategory {
  HIPAA = 'HIPAA',
  GDPR = 'GDPR',
  CCPA_CPRA = 'CCPA/CPRA',
  PCI_DSS = 'PCI/DSS',
  GLBA = 'GLBA',
  FERPA = 'FERPA',
}

export const SensitiveDataCategoryOptions: UiOption[] = Object.values(SensitiveDataCategory).map(
  (item) => ({
    label: item,
    value: item,
  })
);

export type SdEntityConfig = {
  name: string;
  entityType: string;
  categories: SensitiveDataCategory[];
  scanTypes?: ScanTypeConfig;
};

export const SensitiveDataEntityConfig: SdEntityConfig[] = [
  {
    name: 'Phone Number',
    entityType: 'PHONE_NUMBER',
    categories: [
      SensitiveDataCategory.HIPAA,
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Credit Card',
    entityType: 'CREDIT_CARD',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.PCI_DSS],
  },
  { name: 'Crypto Address', entityType: 'CRYPTO', categories: [SensitiveDataCategory.GDPR] },
  {
    name: 'Email Address',
    entityType: 'EMAIL_ADDRESS',
    categories: [
      SensitiveDataCategory.HIPAA,
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
      SensitiveDataCategory.FERPA,
    ],
  },
  {
    name: 'IBAN Code',
    entityType: 'IBAN_CODE',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.PCI_DSS],
  },
  {
    name: 'IP Address',
    entityType: 'IP_ADDRESS',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Person',
    entityType: 'PERSON',
    scanTypes: {
      1: {
        label: 'Rapid',
        tooltip: 'Most restrictive',
        description:
          'Provides the fastest response time, but may have a higher false-positive rate in some cases. Detected person entities will be verified only if no other entity types are detected.',
      },
      2: {
        label: 'Balanced',
        description:
          'Provides a balanced response time with fewer false positives. Detected person entities will be verified for all requests.',
      },
      3: {
        label: 'Context-aware',
        tooltip: 'Least restrictive',
        description:
          'Public figures will be ignored. Ensures the fewest number of false positives, but provides slightly slower response times. Detected person entities will be verified for all requests.',
      },
    },
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Medical License',
    entityType: 'MEDICAL_LICENSE',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.HIPAA,
    ],
  },
  {
    name: 'URL',
    entityType: 'URL',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'US Bank Number',
    entityType: 'US_BANK_NUMBER',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.PCI_DSS,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: "US Driver's License Number",
    entityType: 'US_DRIVER_LICENSE',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.HIPAA,
    ],
  },
  {
    name: 'US Individual Taxpayer Identification Number',
    entityType: 'US_ITIN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'US Passport Number',
    entityType: 'US_PASSPORT',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.HIPAA,
    ],
  },
  {
    name: 'US Social Security Number',
    entityType: 'US_SSN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
      SensitiveDataCategory.HIPAA,
      SensitiveDataCategory.FERPA,
    ],
  },
  {
    name: 'UK NHS Number',
    entityType: 'UK_NHS',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.HIPAA],
  },
  {
    name: 'Spanish Tax Identification Number',
    entityType: 'ES_NIF',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Italian Fiscal Code',
    entityType: 'IT_FISCAL_CODE',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  // { label: "Italian Driver's License Number", value: 'IT_DRIVER_LICENSE' },
  {
    name: 'Italian VAT Code',
    entityType: 'IT_VAT_CODE',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Italian Passport Number',
    entityType: 'IT_PASSPORT',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Italian Identity Card Number',
    entityType: 'IT_IDENTITY_CARD',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Polish National Identification Number',
    entityType: 'PL_PESEL',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Singapore NRIC Number',
    entityType: 'SG_NRIC_FIN',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Singapore Entity Identification Number',
    entityType: 'SG_UEN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Australian Business Registration Number',
    entityType: 'AU_ABN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Australian Company Number',
    entityType: 'AU_ACN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Australian Tax File Number',
    entityType: 'AU_TFN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Australian Medicare Number',
    entityType: 'AU_MEDICARE',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.HIPAA,
    ],
  },
  {
    name: 'Indian Permanent Account Number',
    entityType: 'IN_PAN',
    categories: [
      SensitiveDataCategory.GDPR,
      SensitiveDataCategory.CCPA_CPRA,
      SensitiveDataCategory.GLBA,
    ],
  },
  {
    name: 'Indian Aadhaar Number',
    entityType: 'IN_AADHAAR',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
  {
    name: 'Indian Vehicle Registration Number',
    entityType: 'IN_VEHICLE_REGISTRATION',
    categories: [SensitiveDataCategory.GDPR, SensitiveDataCategory.CCPA_CPRA],
  },
];

export const getSdEntityConfig = (entityType: string): SdEntityConfig | null =>
  SensitiveDataEntityConfig.find((config) => config.entityType === entityType) || null;

export const getSensitiveDataTypeOptions = (
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): UiOption[] => {
  const inheritsFromOrg = policy.inherits();
  const isGroupScope = policy.scope === 'group';

  const orgPolicyDisabledTypes = orgPolicy?.allDisabledSensitiveDataEntityTypes || new Set();

  return SensitiveDataEntityConfig.map(({ name, entityType, scanTypes, categories }) => {
    const disabled = inheritsFromOrg && orgPolicyDisabledTypes.has(entityType);
    return {
      label: name,
      value: entityType,
      meta: { hasSettings: !isGroupScope && !!scanTypes, categories },
      disabled,
      tooltip: disabled ? 'You cannot enable an entity that is disabled by the default policy' : '',
    };
  });
};

export const getSensitiveDataActionOptions = (
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): UiOption[] => {
  const inheritsFromOrg = policy.inherits();
  const orgPolicyActionLevel = orgPolicy?.sensitiveData.action
    ? getSensitiveDataActionLevel(orgPolicy?.sensitiveData.action)
    : 0;

  const options: UiOption[] = [];

  ControlActionCategories.forEach(({ label: catLabel, value: catValue }) => {
    options.push({ label: catLabel, value: catValue, meta: { header: true } });

    SensitiveDataActions.filter(({ category }) => category === catValue).forEach((action) => {
      const { label, value } = action;
      const compLevel = getSensitiveDataActionLevel(value);

      const option: UiOption = {
        label,
        value,
        meta: { action },
      };

      // group policies can only define a less-restrictive action than their parent org policy
      if (inheritsFromOrg && orgPolicyActionLevel) {
        if (compLevel > orgPolicyActionLevel) {
          option.disabled = true;
          option.meta = { blockedByOrgPolicy: true };
        }

        if (compLevel === orgPolicyActionLevel) {
          option.meta = { orgPolicyLevel: true };
        }
      }

      options.push(option);
    });
  });

  return options;
};
